import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  previewProductCatchup,
  approveProductCatchup,
  getProductJourneyActivity,
  type CatchupPreview,
  type CatchupResult,
} from "../../services/productJourneys.service";
export default function ProductJourneyCatchup({
  sequenceId,
  active,
}: {
  sequenceId: string;
  active: boolean;
}) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState<CatchupPreview | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<CatchupResult[] | null>(null);
  const activity = useQuery({
    queryKey: ["product-activity", sequenceId],
    queryFn: () => getProductJourneyActivity(sequenceId),
  });
  const reset = () => {
    setPreview(null);
    setSelected([]);
    setReviewed(false);
    setResult(null);
    setError("");
  };
  const message = (e: unknown) =>
    e instanceof Error
      ? e.message
      : String((e as { message?: string })?.message ?? e);
  async function load() {
    setBusy(true);
    reset();
    try {
      setPreview(await previewProductCatchup(sequenceId, from, to));
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  async function approve() {
    if (!preview || !reviewed) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      setResult(await approveProductCatchup(preview.batch_id, selected));
      await activity.refetch();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="space-y-4 rounded-lg border bg-white p-5 mb-6">
      <h2 className="text-lg font-semibold">Review missed product welcomes</h2>
      <p>
        Choose subscription start dates to preview existing members. These dates
        come from the account’s active subscription records; they may differ
        from the date a product was added. Previewing does not enroll anyone.
        Approving selected recipients starts this journey and may send its
        welcome email immediately. Previous matching email activity and
        exclusions are shown below.
      </p>
      <div className="flex flex-wrap gap-4">
        <label>
          Subscription start from
          <input
            aria-label="Subscription start from"
            className="block border rounded p-2"
            type="date"
            value={from}
            disabled={busy}
            onChange={(e) => {
              reset();
              setFrom(e.target.value);
            }}
          />
        </label>
        <label>
          Subscription start through
          <input
            aria-label="Subscription start through"
            className="block border rounded p-2"
            type="date"
            value={to}
            disabled={busy}
            onChange={(e) => {
              reset();
              setTo(e.target.value);
            }}
          />
        </label>
        <button
          type="button"
          className="border rounded px-4 disabled:opacity-50"
          disabled={busy || !from || !to || from > to}
          onClick={load}
        >
          {busy ? "Working…" : "Preview recipients"}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {preview && !result && (
        <>
          <p>
            {preview.candidates.length} candidates. Select up to 100 eligible
            recipients. This preview expires{" "}
            {new Date(preview.expires_at).toLocaleString()}.
          </p>
          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Member</th>
                  <th>Email</th>
                  <th>Subscription started</th>
                  <th>Prior matching email</th>
                  <th>Exclusion</th>
                </tr>
              </thead>
              <tbody>
                {preview.candidates.map((c) => (
                  <tr key={c.contact_id} className="border-t">
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${c.name}`}
                        checked={selected.includes(c.contact_id)}
                        disabled={
                          busy ||
                          !!c.exclusion ||
                          (!selected.includes(c.contact_id) &&
                            selected.length >= 100)
                        }
                        onChange={(e) => {
                          setReviewed(false);
                          setSelected((old) =>
                            e.target.checked
                              ? [...old, c.contact_id]
                              : old.filter((x) => x !== c.contact_id),
                          );
                        }}
                      />
                    </td>
                    <td>{c.name}</td>
                    <td>{c.email || "Missing"}</td>
                    <td>{c.acquired_on}</td>
                    <td>
                      {c.prior_matching_email_at
                        ? new Date(c.prior_matching_email_at).toLocaleString()
                        : "None recorded"}
                    </td>
                    <td>
                      {c.exclusion?.replaceAll("_", " ") ||
                        "Eligible at preview"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <label className="block">
            <input
              type="checkbox"
              checked={reviewed}
              disabled={busy || selected.length === 0}
              onChange={(e) => setReviewed(e.target.checked)}
            />{" "}
            I reviewed these {selected.length} recipients and approve enrolling
            them.
          </label>
          {!active && (
            <p>
              The journey must be active before catch-up can be approved. Create
              a fresh preview after activation.
            </p>
          )}
          <button
            type="button"
            className="bg-blue-700 text-white rounded px-4 py-2 disabled:opacity-50"
            disabled={busy || !active || !reviewed || selected.length === 0}
            onClick={approve}
          >
            Enroll {selected.length} selected members
          </button>
        </>
      )}
      {result && (
        <div role="status">
          <p>
            {result.filter((r) => r.outcome === "enrolled").length} enrolled;{" "}
            {result.filter((r) => r.outcome !== "enrolled").length} skipped
            after rechecking.
          </p>
          <ul>
            {result
              .filter((r) => r.outcome !== "enrolled")
              .map((r) => (
                <li key={r.contact_id}>
                  {
                    preview?.candidates.find(
                      (c) => c.contact_id === r.contact_id,
                    )?.name
                  }
                  : {r.outcome.replaceAll("_", " ")}
                </li>
              ))}
          </ul>
        </div>
      )}
      <h3 className="font-semibold">Recent product changes</h3>
      {activity.isError ? (
        <p role="alert">Unable to load product activity.</p>
      ) : activity.data?.length ? (
        <ul>
          {activity.data.map((e) => (
            <li key={e.id}>
              {new Date(e.observed_at).toLocaleString()} — added{" "}
              {e.added_products.join(", ") || "none"}; {e.status}
              {e.last_error ? `: ${e.last_error}` : ""}
              <ul>
                {e.product_sequence_decisions
                  .filter((d) => d.sequence_id === sequenceId)
                  .map((d) => (
                    <li key={d.contact_id}>
                      {[d.contacts?.first_name, d.contacts?.last_name]
                        .filter(Boolean)
                        .join(" ") || "Member"}
                      : {d.outcome.replaceAll("_", " ")}
                    </li>
                  ))}
              </ul>
            </li>
          ))}
        </ul>
      ) : (
        <p>No product changes recorded for this journey yet.</p>
      )}
    </section>
  );
}
